import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './context/authContext';
import { LanguageProvider } from './context/languageContext';
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import { SnackbarProvider } from './context/snackbarContext';
import { Toaster } from '@/components/ui/toaster';
import ErrorBoundary from './components/ErrorBoundary';
const PWAInstallBanner = React.lazy(() => import('./components/PWAInstallBanner'));
import { queryClient } from './lib/queryClient';
import './i18n';
import './index.css';
import './App.css';
import { CookieManager } from 'react-cookie-manager';
import 'react-cookie-manager/style.css';
import { useTranslation } from 'react-i18next';

// Eager-load the lightweight landing page, lazy-load heavier authenticated pages for better initial load performance
import Index from './pages/Index';

const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const HomePage = React.lazy(() => import('./pages/HomePage'));
const ListDetailPage = React.lazy(() => import('./pages/ListDetailPage'));
const AccountSettingsPage = React.lazy(() => import('./pages/AccountSettingsPage'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));

const LoadingFallback: React.FC = () => (
  <div className="flex justify-center items-center h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500 border-solid" />
  </div>
);

const App: React.FC = () => {
  const { t } = useTranslation();

  // Listen for service worker messages and logs
  useEffect(() => {
    const handleSWMessage = (event: MessageEvent) => {
      if (event.data?.type === 'SW_READY') {
        console.log('[App] 🎉 Service Worker is ready!', event.data.data);
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleSWMessage);

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
    };
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <AuthProvider>
            <SnackbarProvider>
              <CookieManager
                cookieKitId="689af594630cbf2991c86e14"
                displayType="banner"
                translations={{
                  title: '',
                  message: (
                    <span className="flex items-start gap-3">
                      <img src="/cookie.png" alt="Cookie" className="h-12 w-12 sm:h-14 sm:w-14" />
                      <span className="text-left">
                        <strong className="block">{t('cookie.title')}</strong>
                        <span className="block">{t('cookie.message')}</span>
                      </span>
                    </span>
                  ) as unknown as string,
                }}
              >
                <Router>
                  <ScrollToTop />
                  <Header />
                  <Routes>
                    <Route
                      path="/login"
                      element={
                        <Suspense fallback={<LoadingFallback />}>
                          <LoginPage />
                        </Suspense>
                      }
                    />
                    <Route path="/" element={<Index />} />
                    <Route
                      path="/terms"
                      element={
                        <Suspense fallback={<LoadingFallback />}>
                          <TermsOfService />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/privacy"
                      element={
                        <Suspense fallback={<LoadingFallback />}>
                          <PrivacyPolicy />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/app"
                      element={
                        <ProtectedRoute>
                          <Suspense fallback={<LoadingFallback />}>
                            <HomePage />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/lists/:id"
                      element={
                        <ProtectedRoute>
                          <Suspense fallback={<LoadingFallback />}>
                            <ListDetailPage />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/account-settings"
                      element={
                        <ProtectedRoute>
                          <Suspense fallback={<LoadingFallback />}>
                            <AccountSettingsPage />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="*"
                      element={
                        <Suspense fallback={<LoadingFallback />}>
                          <NotFound />
                        </Suspense>
                      }
                    />
                  </Routes>
                  <Toaster />
                  <Footer />

                  {/* PWA Install Banner */}
                  <Suspense fallback={null}>
                    <PWAInstallBanner />
                  </Suspense>

                  {/* PWA Test Utils - Only in development */}
                  {/* <PWATestUtils /> */}

                  {/* Service Worker Migration Prompt - Temporarily disabled for testing */}
                  {/*
                  <ServiceWorkerMigrationPrompt
                    onMigrationComplete={() => {
                      console.log('[App] 🎉 Service worker migration completed!');
                    }}
                    onMigrationSkipped={() => {
                      console.log('[App] ⏭️ Service worker migration skipped');
                    }}
                    autoStart={false}
                  />
                  */}

                  {/* Service Worker Update Prompt */}
                  {/* <ServiceWorkerUpdatePrompt /> */}
                </Router>
              </CookieManager>
            </SnackbarProvider>
          </AuthProvider>
        </LanguageProvider>

        {/* Dev tools - only in development */}
        {process.env.NODE_ENV === 'development' && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
